import { useMemo, type ReactNode } from 'react';
import { EntityLink } from '../primitives/EntityLink';
import { useReferenceLabels } from '@resource-kit/controllers/react';

/**
 * The READ-ONLY half of a many2one field (design §5.11).
 *
 * The record already holds the foreign id, so the display name comes from the
 * batch reference read and the value renders as a link to the target record.
 * The id is what is shown until (and unless) a name arrives, because the id is
 * the one thing always true — and it is exactly what this slot showed before
 * the relation existed, which makes it the right degraded state as well.
 */
export function RelationValue({ model, value }: { model: string; value: unknown }): ReactNode {
  const id = typeof value === 'string' && value !== '' ? value : null;
  // A field with no reference must not produce a request: the memo keeps the
  // empty array stable so `useReferenceLabels` sees one unchanging key.
  const ids = useMemo(() => (id === null ? [] : [id]), [id]);
  const { source, labels } = useReferenceLabels(model, ids);

  if (id === null) return <span className="text-muted-foreground">—</span>;

  const label = labels.get(id);
  if (label === undefined || source === null) {
    return <span className="font-mono text-xs break-all">{id}</span>;
  }
  return <EntityLink to={source.detailPath(id)}>{label}</EntityLink>;
}
